import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';
import { enqueueSnackbar } from 'notistack';

const DuckDBContext = createContext(null);

export const useDuckDB = () => {
  const context = useContext(DuckDBContext);
  if (!context) {
    throw new Error('useDuckDB must be used within DuckDBProvider');
  }
  return context;
};

export const DuckDBProvider = ({ children, config }) => {
  const [db, setDb] = useState(null);
  const [connection, setConnection] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const initPromiseRef = useRef(null);

  const initDuckDB = useCallback(async () => {
    // Prevent multiple initialization attempts
    if (initPromiseRef.current) {
      return initPromiseRef.current;
    }

    setIsLoading(true);
    setError(null);
    let attempts = 0;

    const initAttempt = async () => {
      try {
        console.log('🚀 Initializing DuckDB-WASM...');

        // ✅ FIX: Use official bundles (NO manual config)
        const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
        const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);

        // const bundle = {
        //   mainModule: '@duckdb/duckdb-wasm/dist/duckdb/duckdb-browser-eh.wasm',
        //   mainWorker: '@duckdb/duckdb-wasm/dist/duckdb/duckdb-browser-eh.worker.js',
        // };

        const workerResponse = await fetch(bundle.mainWorker);
        const workerText = await workerResponse.text();
        const workerBlob = new Blob([workerText], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(workerBlob);

        const worker = new Worker(workerUrl);

        const logger = new duckdb.ConsoleLogger();
        const dbInstance = new duckdb.AsyncDuckDB(logger, worker);

        await dbInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);

        // Create connection
        const conn = await dbInstance.connect();
        console.log('✅ DuckDB connection created');

        try {
          await conn.query(`INSTALL httpfs;`);
          await conn.query(`LOAD httpfs;`);
          await conn.query(`SET enable_http_metadata_cache=false;`);
          await conn.query(`SET enable_object_cache=false;`);
          await conn.query(`SET threads=1;`);
          await conn.query(`SET s3_region='us-east-1';`);
        } catch (extError) {
          console.warn('⚠️ httpfs extension not available:', extError.message);
        }

        setDb(dbInstance);
        setConnection(conn);
        setIsInitialized(true);
        setError(null);
        setRetryCount(0);

        console.log('🎉 DuckDB-WASM initialized successfully!');

        return { db: dbInstance, conn };
      } catch (error) {
        console.error('❌ Failed to initialize DuckDB-WASM:', error);
        setError(error.message);

        if (attempts < maxRetries) {
          attempts++;
          setRetryCount(attempts);
          console.log(`Retry attempt ${attempts}/${maxRetries}...`);
          await new Promise((r) => setTimeout(r, 1000 * attempts));
          return initAttempt();
        }

        // enqueueSnackbar('Failed to initialize data profiling engine', {
        //   variant: 'error',
        //   autoHideDuration: 5000,
        // });
        throw error;
      } finally {
        setIsLoading(false);
      }
    };

    initPromiseRef.current = initAttempt();
    return initPromiseRef.current;
  }, [config, maxRetries]);

  useEffect(() => {
    initDuckDB();

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up DuckDB resources...');
      if (connection) {
        try {
          connection.close();
          console.log('Connection closed');
        } catch (e) {
          console.warn('Error closing connection:', e);
        }
      }
      if (db) {
        try {
          db.terminate();
          console.log('Database terminated');
        } catch (e) {
          console.warn('Error terminating db:', e);
        }
      }
      initPromiseRef.current = null;
    };
  }, [initDuckDB]);

  const testConnection = useCallback(async () => {
    if (!connection || !isInitialized) {
      console.warn('DuckDB not initialized');
      return null;
    }

    try {
      const result = await connection.query(`
        SELECT 
          'DuckDB Connection Test' as test_name,
          CURRENT_TIMESTAMP as timestamp,
          VERSION() as version,
          '✅ Connection is healthy' as status
      `);
      const testResult = result.toArray();
      console.log('DuckDB connection test:', testResult);
      return testResult;
    } catch (error) {
      console.error('Connection test failed:', error);
      return null;
    }
  }, [connection, isInitialized]);

  const executeQuery = useCallback(
    async (sql) => {
      if (!connection || !isInitialized) {
        throw new Error('DuckDB not initialized');
      }

      try {
        // console.log('Executing DuckDB query:', sql);
        const startTime = performance.now();
        const result = await connection.query(sql);
        const endTime = performance.now();
        const data = result.toArray();
        console.log(`✅ Query executed in ${(endTime - startTime).toFixed(2)}ms, returned ${data.length} rows`);
        return data;
      } catch (error) {
        console.error('Query execution failed:', error);
        throw error;
      }
    },
    [connection, isInitialized]
  );

  const registerFile = useCallback(
    async (fileName, content, isBinary = false) => {
      if (!db || !isInitialized) {
        throw new Error('DuckDB not initialized');
      }

      try {
        if (isBinary) {
          await db.registerFileBuffer(fileName, content);
        } else {
          await db.registerFileText(fileName, content);
        }
        return true;
      } catch (error) {
        console.error('File registration failed:', error);
        throw error;
      }
    },
    [db, isInitialized]
  );

  const value = {
    db,
    connection,
    isInitialized,
    isLoading,
    error,
    retryCount,
    maxRetries,
    testConnection,
    executeQuery,
    registerFile,
    reinitialize: initDuckDB,
  };

  return <DuckDBContext.Provider value={value}>{children}</DuckDBContext.Provider>;
};
